/* ==========================================================================
   JADUZAR INK — MASTER BEHAVIOUR

   Two enhancements, both optional. With JavaScript off:
     · the canvas index is still a complete, linked list;
     · the product page still shows the Bali branch with its native Shopify
       product form, so booking and checkout keep working.
   ========================================================================== */
(() => {
  'use strict';

  /* ----------------------------------------------------------------------
     Canvas index — swap the preview photograph on hover/focus.
     Decorative only: every label and link lives in the list itself.
     ---------------------------------------------------------------------- */
  const initCanvas = (root) => {
    const preview = root.querySelector('[data-ji-canvas-preview]');
    if (!preview) return;

    const shots = Array.from(preview.querySelectorAll('img'));
    const links = Array.from(root.querySelectorAll('[data-ji-canvas-index]'));
    if (!shots.length || !links.length) return;

    const show = (i) => {
      shots.forEach((img, n) => img.classList.toggle('is-shown', n === i));
    };

    links.forEach((link) => {
      const i = Number(link.dataset.jiCanvasIndex);
      link.addEventListener('mouseenter', () => show(i));
      link.addEventListener('focus', () => show(i));
    });
  };

  /* ----------------------------------------------------------------------
     Product page — experience branches.

     Bali    : the real Shopify variant form. Nothing here touches it beyond
               showing and hiding it, so the existing booking logic is intact.
     Nether. : a builder over a separate day-session product. Totals are
               computed from cents supplied by Liquid — never parsed back out
               of formatted text.
     ---------------------------------------------------------------------- */
  const initExperience = (root) => {
    const buttons = Array.from(root.querySelectorAll('[data-ji-xp]'));
    const branches = Array.from(root.querySelectorAll('[data-ji-branch]'));
    if (!buttons.length || !branches.length) return;

    // The headline price is part of the state, not decoration: a Bali amount
    // must never be on screen while the Netherlands panel is the active one.
    const priceBali = root.querySelector('[data-ji-price-bali]');
    const priceNl = root.querySelector('[data-ji-price-nl]');

    const select = (name) => {
      const isNl = name === 'netherlands';
      if (priceBali && priceNl) {
        priceBali.hidden = isNl;
        priceNl.hidden = !isNl;
      }

      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.jiXp === name)));
      branches.forEach((b) => {
        const on = b.dataset.jiBranch === name;
        b.hidden = !on;
        // Keep the hidden branch out of the tab order and out of submission.
        b.querySelectorAll('input, select, button, a').forEach((el) => {
          if (on) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      });
    };

    buttons.forEach((b) => {
      b.addEventListener('click', () => select(b.dataset.jiXp));
    });

    // Deep links from the homepage cards: ?experience=netherlands
    const wanted = new URLSearchParams(window.location.search).get('experience');
    const initial =
      wanted && buttons.some((b) => b.dataset.jiXp === wanted)
        ? wanted
        : buttons[0].dataset.jiXp;
    select(initial);
  };

  const initNetherlands = (panel) => {
    const dayPrice = Number(panel.dataset.jiDayPrice || 0);
    const numbPrice = Number(panel.dataset.jiNumbingPrice || 0);
    const currency = panel.dataset.jiCurrency || 'EUR';
    const locale = panel.dataset.jiLocale || 'en';

    const out = panel.querySelector('[data-ji-days-out]');
    const qty = panel.querySelector('[data-ji-days-input]');
    const minus = panel.querySelector('[data-ji-days="-1"]');
    const plus = panel.querySelector('[data-ji-days="1"]');
    const comfortBtn = panel.querySelector('[data-ji-comfort]');
    const comfortField = panel.querySelector('[data-ji-comfort-input]');
    const rowSessions = panel.querySelector('[data-ji-sum-sessions]');
    const rowComfort = panel.querySelector('[data-ji-sum-comfort]');
    const rowTotal = panel.querySelector('[data-ji-sum-total]');
    const noteComfort = panel.querySelector('[data-ji-comfort-note]');

    const money = (cents) => {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(0);
      }
    };

    let days = Number(qty && qty.value) || 1;
    let comfort = false;

    const render = () => {
      if (qty) qty.value = String(days);
      if (out) {
        // [count] is the placeholder. The older {{ count }} form is still
        // accepted so a stored setting from an earlier version keeps working.
        out.textContent =
          days === 1
            ? panel.dataset.jiDayOne
            : (panel.dataset.jiDayMany || '[count] days')
                .replace('[count]', days)
                .replace('{{ count }}', days);
      }
      if (minus) minus.disabled = days <= 1;

      if (comfortBtn) comfortBtn.setAttribute('aria-pressed', String(comfort));
      if (comfortField) comfortField.value = comfort ? panel.dataset.jiComfortYes : '';

      if (rowSessions) rowSessions.textContent = days + ' × ' + money(dayPrice);
      if (rowComfort) {
        rowComfort.textContent = comfort
          ? money(numbPrice) + ' · ' + panel.dataset.jiComfortRequested
          : panel.dataset.jiComfortNone;
      }

      // The total includes the numbing request, as specified. Checkout only
      // charges the day sessions, so whenever numbing is selected the note
      // below the total says so — the figure is never left to speak alone.
      const sessionsCents = days * dayPrice;
      if (rowTotal) rowTotal.textContent = money(sessionsCents + (comfort ? numbPrice : 0));
      if (noteComfort) noteComfort.hidden = !comfort;

      // Announce the state so the payment choice and the medical marker can
      // react without either of them re-deriving the totals. Nothing in this
      // file acts on the event; if no one listens, the panel behaves exactly
      // as it did before.
      panel.dispatchEvent(
        new CustomEvent('ji:nl', {
          bubbles: true,
          detail: {
            days,
            comfort,
            sessionsCents,
            sessionsMoney: money(sessionsCents),
            money,
            panel,
          },
        })
      );
    };

    if (minus) minus.addEventListener('click', () => { days = Math.max(1, days - 1); render(); });
    if (plus) plus.addEventListener('click', () => { days = Math.min(30, days + 1); render(); });
    if (comfortBtn) comfortBtn.addEventListener('click', () => { comfort = !comfort; render(); });

    render();
  };

  /* ----------------------------------------------------------------------
     Carry the chosen experience from the homepage cards through the
     catalogue to the project page. Purely additive: if this never runs, the
     links still go where they should and the product page opens on Bali.
     ---------------------------------------------------------------------- */
  const carryExperience = () => {
    const wanted = new URLSearchParams(window.location.search).get('experience');
    if (!wanted) return;

    document.querySelectorAll('[data-ji-experience-link]').forEach((a) => {
      try {
        const url = new URL(a.href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        url.searchParams.set('experience', wanted);
        a.href = url.pathname + url.search + url.hash;
      } catch (e) {
        /* leave the link exactly as authored */
      }
    });
  };

  /* Language select submits itself; the visible button is the no-JS path. */
  const initLanguage = (select) => {
    const form = select.form;
    if (!form) return;
    form.classList.add('ji-lang-form--auto');
    select.addEventListener('change', () => form.submit());
  };

  const boot = () => {
    carryExperience();
    document.querySelectorAll('[data-ji-lang]').forEach(initLanguage);
    document.querySelectorAll('[data-ji-canvas]').forEach(initCanvas);

    // Bind the experience switch to its declared container. If that attribute
    // ever goes missing again, fall back to the document rather than leaving
    // the buttons inert — a switch that renders must always switch.
    const groups = document.querySelectorAll('[data-ji-experience]');
    if (groups.length) {
      groups.forEach(initExperience);
    } else if (document.querySelector('[data-ji-xp]')) {
      initExperience(document);
    }
    document.querySelectorAll('[data-ji-nl-panel]').forEach(initNetherlands);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify's theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
